/**
 * Images Page - View all images and permanently delete from storage
 *
 * One place for Tammy to see every image (menu, gallery, special menus, events, uploads)
 * and permanently remove files from storage when she no longer needs them.
 * Deleting from storage is irreversible; if the image is still used somewhere,
 * that place will show a broken image until she picks another.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiTrash, HiRefresh, HiExclamation, HiX, HiArrowCircleRight } from 'react-icons/hi';
import { adminFetch } from '../lib/adminApi';

interface UsedByEntry {
  source: string;
  label: string;
  /** Admin path to open (e.g. /admin/menu/123); when set, label is clickable */
  path?: string;
}

interface ImageItem {
  id: string;
  url: string;
  name: string;
  source: 'menu' | 'gallery' | 'collection' | 'event' | 'uploads';
  category: string;
  type: string;
  storageFilename?: string;
  usedBy?: UsedByEntry[];
  isUnused?: boolean;
}

type SourceFilter = 'all' | 'menu' | 'gallery' | 'collection' | 'event' | 'uploads' | 'unused';

const SOURCE_LABELS: Record<string, string> = {
  menu: 'Menu',
  gallery: 'Gallery',
  collection: 'Special Menus',
  event: 'Events',
  uploads: 'In storage (can delete)',
};

export default function Images() {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [sources, setSources] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
    filename: string;
    usedBy: UsedByEntry[];
    isUnused?: boolean;
  } | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/images/all');
      if (!res.ok) throw new Error('Failed to load images');
      const data = await res.json();
      setImages(data.images || []);
      setSources(data.sources || {});
    } catch (err) {
      console.error('Error fetching images:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const filtered = images.filter((img) => {
    const matchSource =
      sourceFilter === 'all'
        ? true
        : sourceFilter === 'unused'
          ? img.source === 'uploads' && img.isUnused === true
          : img.source === sourceFilter;
    const matchSearch =
      !search || img.name.toLowerCase().includes(search.toLowerCase());
    return matchSource && matchSearch;
  });

  /** Max "Used in" entries to show inline before "+N more" */
  const USED_BY_MAX_SHOW = 2;

  const handleDeleteFromStorage = async () => {
    if (!confirmDelete) return;
    const { filename } = confirmDelete;
    setDeletingId(confirmDelete.id);
    try {
      const res = await adminFetch(`/api/upload/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.details || 'Delete failed');
      }
      setConfirmDelete(null);
      await fetchImages();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete from storage');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRotate = async (filename: string, id: string) => {
    setRotatingId(id);
    try {
      const res = await adminFetch(
        `/api/upload/${encodeURIComponent(filename)}/rotate?degrees=90`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.details || 'Rotate failed');
      }
      await fetchImages();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to rotate image');
    } finally {
      setRotatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All Images</h1>
        <p className="text-gray-500 mt-1">
          View every image used on your site and in storage. Permanently delete
          files from storage only when you no longer need them (they can’t be
          reused after that).
        </p>
        <p className="text-gray-500 mt-1 text-sm">
          To add or edit gallery photos (titles, categories, featured), use{' '}
          <strong>Gallery</strong> in the sidebar.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {(['all', 'unused', 'menu', 'gallery', 'collection', 'event', 'uploads'] as const).map(
            (src) => (
              <button
                key={src}
                onClick={() => setSourceFilter(src)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  sourceFilter === src
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {src === 'all' ? 'All' : src === 'unused' ? 'Unused only' : SOURCE_LABELS[src] || src}
                {src !== 'all' && src !== 'unused' && sources[src] != null && ` (${sources[src]})`}
                {src === 'unused' && sources.unused != null && ` (${sources.unused})`}
              </button>
            )
          )}
        </div>
        <input
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
        />
        <button
          onClick={fetchImages}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
        >
          <HiRefresh className={loading ? 'animate-spin w-4 h-4' : 'w-4 h-4'} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="py-12 text-center text-gray-500">Loading images...</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          No images match the current filters.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map((img) => (
            <div
              key={img.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="aspect-square bg-gray-100 relative">
                <img
                  src={img.url}
                  alt={img.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text x="50" y="50" text-anchor="middle" fill="%23999" font-size="12">No image</text></svg>';
                  }}
                />
                {img.source === 'uploads' && (
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded">
                    In storage
                  </span>
                )}
                {img.isUnused && (
                  <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-green-600 text-white text-xs rounded">
                    Unused (safe to delete)
                  </span>
                )}
              </div>
              <div className="p-2">
                <p className="text-sm font-medium text-gray-900 truncate" title={img.name}>
                  {img.name}
                </p>
                <p className="text-xs text-gray-500">{SOURCE_LABELS[img.source] || img.source}</p>
                {(img.usedBy?.length ?? 0) > 0 ? (
                  <p className="text-xs text-gray-600 mt-1" title={img.usedBy!.map(u => u.label).join(', ')}>
                    Used in:{' '}
                    {img.usedBy!.slice(0, USED_BY_MAX_SHOW).map((u, i) => (
                      <span key={`${u.source}-${u.label}-${i}`}>
                        {i > 0 && ', '}
                        {u.path ? (
                          <Link to={u.path} className="text-purple-600 hover:text-purple-800 hover:underline">
                            {u.label}
                          </Link>
                        ) : (
                          u.label
                        )}
                      </span>
                    ))}
                    {img.usedBy!.length > USED_BY_MAX_SHOW && (
                      <span className="text-gray-500"> +{img.usedBy!.length - USED_BY_MAX_SHOW} more</span>
                    )}
                  </p>
                ) : img.source === 'uploads' ? (
                  <p className="text-xs text-gray-500 mt-1 italic">Not used anywhere</p>
                ) : null}
                {img.storageFilename && (
                  <div className="mt-2 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleRotate(img.storageFilename!, img.id)}
                      disabled={rotatingId === img.id}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded border border-purple-200 hover:border-purple-300"
                      title="Rotate 90° clockwise"
                    >
                      {rotatingId === img.id ? (
                        'Rotating...'
                      ) : (
                        <>
                          <HiArrowCircleRight className="w-3.5 h-3.5" />
                          Rotate 90°
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmDelete({
                          id: img.id,
                          name: img.name,
                          filename: img.storageFilename!,
                          usedBy: img.usedBy ?? [],
                          isUnused: img.isUnused,
                        })
                      }
                      disabled={deletingId === img.id}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded border border-red-200 hover:border-red-300"
                    >
                      {deletingId === img.id ? (
                        'Deleting...'
                      ) : (
                        <>
                          <HiTrash className="w-3.5 h-3.5" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <HiExclamation className="w-6 h-6 flex-shrink-0" />
              <h3 className="text-lg font-semibold">Permanently delete from storage?</h3>
            </div>
            <p className="text-gray-600 text-sm mb-2">
              This will remove the file <strong>“{confirmDelete.name}”</strong> from
              storage. You cannot undo this.
            </p>
            {confirmDelete.usedBy.length > 0 ? (
              <p className="text-gray-600 text-sm mb-4">
                This image is still used in:{' '}
                <strong>
                  {confirmDelete.usedBy.map((u, i) => (
                    <span key={`${u.source}-${u.label}-${i}`}>
                      {i > 0 && ', '}
                      {u.path ? (
                        <Link to={u.path} className="text-purple-600 hover:text-purple-800 hover:underline" onClick={() => setConfirmDelete(null)}>
                          {u.label}
                        </Link>
                      ) : (
                        `${u.source}: ${u.label}`
                      )}
                    </span>
                  ))}
                </strong>
                . Those will show a broken image until you pick a different image.
              </p>
            ) : (
              <p className="text-green-700 text-sm mb-4 font-medium">
                This image is not used anywhere. Safe to delete.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <HiX className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteFromStorage}
                disabled={deletingId !== null}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <HiTrash className="w-4 h-4" />
                {deletingId ? 'Deleting...' : 'Delete from storage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
